import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';

// Layout
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Homepage sections
import HeroSection from './components/homepage/HeroSection';
import ProblemSection from './components/homepage/ProblemSection';
import WhatWeDoSection from './components/homepage/WhatWeDoSection';
import HowWeWorkSection from './components/homepage/HowWeWorkSection';
import ClientOutputsSection from './components/homepage/ClientOutputsSection';
import ServicesSection from './components/homepage/ServicesSection';
import SectorsSection from './components/homepage/SectorsSection';
import EvidenceSection from './components/homepage/EvidenceSection';
import FounderSection from './components/homepage/FounderSection';
import AboutSection from './components/homepage/AboutSection';
import FeaturedResultsSection from './components/homepage/FeaturedResultsSection';
import FeaturedInsightsSection from './components/homepage/FeaturedInsightsSection';
import FinalCTASection from './components/homepage/FinalCTASection';

// Pages
import HowWeWorkPage from './pages/HowWeWorkPage';
import ServicesPage from './pages/ServicesPage';
import ResultsPage from './pages/ResultsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import InsightsPage from './pages/InsightsPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import NotFoundPage from './pages/NotFoundPage';
import STREnterprisesPage from './pages/case-studies/STREnterprisesPage';
import DynatechPage from './pages/case-studies/DynatechPage';
import BELEngineeringPage from './pages/case-studies/BELEngineeringPage';
import CPTPage from './pages/case-studies/CPTPage';

// Existing pages (preserved)
import PrivacyPolicy from './PrivacyPolicy';
import VideoLandingPage from './VideoLandingPage';
import ProductivityPopup from './ProductivityPopup';
import GATest from './GATest';
import { initAllAnalytics, usePageTracking } from './analytics';
import { hasConsented } from './utils/consent';
import CookieConsent from './components/CookieConsent';

/* ───────────────────────── Homepage ───────────────────────── */
const HomePage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="main-website">
      <Header />

      {/* 1. Hero */}
      <HeroSection />
      {/* 2. Common signs */}
      <ProblemSection />
      {/* 3. Founder credibility */}
      <FounderSection />
      {/* 4. What Kalobrate does */}
      <WhatWeDoSection />
      {/* 4. How Kalobrate works */}
      <HowWeWorkSection />
      {/* 5. What the client receives */}
      <ClientOutputsSection />
      {/* 6. Early client evidence (testimonials) */}
      <EvidenceSection />
      {/* 7. Main areas of support */}
      <ServicesSection />
      {/* 8. Relevant sectors */}
      <SectorsSection />
      {/* 9. Featured case studies */}
      <FeaturedResultsSection />
      {/* 10. About Ollie */}
      <AboutSection />
      {/* 11. Insights preview */}
      <FeaturedInsightsSection />
      {/* 12. Final contact */}
      <FinalCTASection />

      <Footer />

      <ProductivityPopup pageType="homepage" triggerDelay={20000} scrollTrigger={40} />
    </div>
  );
};

/* ───────────────────────── Router ───────────────────────── */
const App = () => {
  useEffect(() => {
    // Only init analytics if user has already consented (returning visitor)
    if (hasConsented()) {
      initAllAnalytics();
    }
  }, []);

  const handleConsentChange = (accepted) => {
    if (accepted) {
      initAllAnalytics();
    }
  };

  return (
    <Router>
      <PageTracker />
      <CookieConsent onConsentChange={handleConsentChange} />
      <Routes>
        {/* Core pages */}
        <Route path="/" element={<HomePage />} />
        <Route path="/how-we-work" element={<HowWeWorkPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/results" element={<ResultsPage />} />
        <Route path="/results/str-enterprises" element={<STREnterprisesPage />} />
        <Route path="/results/dynatech-renewables" element={<DynatechPage />} />
        <Route path="/results/bel-engineering" element={<BELEngineeringPage />} />
        <Route path="/results/cpt" element={<CPTPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/insights" element={<InsightsPage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />

        {/* Existing preserved routes */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/ga-test" element={<GATest />} />
        <Route path="/video-1" element={<VideoLandingPage videoId="1" />} />
        <Route path="/video-2" element={<VideoLandingPage videoId="2" />} />
        <Route path="/video-3" element={<VideoLandingPage videoId="3" />} />
        <Route path="/video-4" element={<VideoLandingPage videoId="4" />} />
        <Route path="/video-5" element={<VideoLandingPage videoId="5" />} />
        <Route path="/video-6" element={<VideoLandingPage videoId="6" />} />
        <Route path="/video-7" element={<VideoLandingPage videoId="7" />} />

        {/* Catch-all 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
};

export { HomePage as MainWebsite };

const PageTracker = () => {
  usePageTracking();
  return null;
};

export default App;
